class BundlerBuilder extends HTMLElement {
  constructor() {
    super();
    // One entry per unit, in the order the shopper picked them, so duplicates
    // simply occupy consecutive slots.
    this.picks = [];
  }

  connectedCallback() {
    this.bundlePropertyKey =
      this.dataset.bundlePropertyKey || "_part_of_bundle";
    this.bundlePropertyValue =
      this.dataset.bundlePropertyValue || "Build your own bundle";

    this.navigation = this.querySelector(".bundler-builder--navigation");
    this.productsGrid = this.querySelector(".bundler-builder--grid");

    this.maxSlots = parseInt(this.dataset.maxSlots, 10) || 6;
    this.tiers = [
      {
        items: parseInt(this.dataset.tier1Items, 10) || 0,
        discount: parseFloat(this.dataset.tier1Discount) || 0,
      },
      {
        items: parseInt(this.dataset.tier2Items, 10) || 0,
        discount: parseFloat(this.dataset.tier2Discount) || 0,
      },
    ]
      .filter((tier) => tier.items > 0 && tier.discount > 0)
      .sort((a, b) => a.items - b.items);
    this.minItems = this.tiers.length ? this.tiers[0].items : 1;

    this.slots = Array.from(
      this.navigation.querySelectorAll(".bundler-builder--nav-item")
    );
    this.addToCartBtn = this.navigation.querySelector(
      "button[data-add-bundle-to-cart]"
    );
    this.ctaLabel = this.navigation.querySelector("[data-byob-cta-label]");
    this.compareEl = this.navigation.querySelector("[data-bundle-compare]");
    this.progressEl = this.navigation.querySelector("[data-byob-progress]");
    // Scoped to the element rather than the panel so the copy inside the mobile
    // sheet is picked up too — it is still a descendant at this point, before
    // the sheet is re-parented to <body> below.
    this.progressFills = Array.from(
      this.querySelectorAll("[data-byob-progress-fill]")
    );
    this.tierMarkers = Array.from(
      this.querySelectorAll("[data-byob-tier-marker]")
    );
    this.summary = this.navigation.querySelector("[data-bundle-summary]");
    this.discountLabelEl = this.navigation.querySelector(
      "[data-bundle-discount-label]"
    );
    this.savingsEl = this.navigation.querySelector("[data-bundle-savings]");

    // Mobile pinned sheet (mirrors the panel slots + total + CTA)
    this.mobileBar = this.querySelector("[data-byob-mobile-bar]");
    this.barSlots = this.querySelector("[data-byob-bar-slots]");
    this.barProgress = this.querySelector("[data-byob-bar-progress]");
    this.barCount = this.querySelector("[data-byob-bar-count]");
    this.barCompare = this.querySelector("[data-byob-bar-compare]");
    this.barCta = this.querySelector("[data-byob-bar-cta]");
    this.barCtaLabel = this.querySelector("[data-byob-bar-cta-label]");
    this.barToggle = this.querySelector("[data-byob-bar-toggle]");

    this.onGridClick = this.gridClickHandler.bind(this);
    this.onNavigationClick = this.navigationClickHandler.bind(this);
    this.onBarClick = this.barClickHandler.bind(this);

    this.productsGrid.addEventListener("click", this.onGridClick);
    this.navigation.addEventListener("click", this.onNavigationClick);

    // Re-parent the fixed bar to <body> so no ancestor stacking context or
    // transform traps it below the panel (which would block taps on the CTA).
    // Keep it always visible on mobile (CSS hides it on desktop); the CTA stays
    // disabled with a "pick more" hint until the bundle is valid.
    if (this.mobileBar) {
      this.mobileBar.hidden = false;
      this.mobileBar.addEventListener("click", this.onBarClick);
      document.body.appendChild(this.mobileBar);
    }

    // Set by hydrateFromCart when the shopper arrives from "Edit my set".
    this.editing = null;

    this.render();

    const editSetId = new URLSearchParams(window.location.search).get(
      "edit_set"
    );
    if (editSetId) this.hydrateFromCart(editSetId);
  }

  disconnectedCallback() {
    this.productsGrid.removeEventListener("click", this.onGridClick);
    this.navigation.removeEventListener("click", this.onNavigationClick);
    // The bar was re-parented to <body>; remove it so a re-render (theme editor)
    // can't leave an orphaned/duplicate bar behind.
    if (this.mobileBar) {
      this.mobileBar.removeEventListener("click", this.onBarClick);
      this.mobileBar.remove();
    }
  }

  /* ──────────────────────────────
     Events
     ────────────────────────────── */
  gridClickHandler(event) {
    const card = event.target.closest(".bundler-builder--grid-item");
    if (!card) return;

    const incBtn = event.target.closest("[data-byob-inc], [data-byob-add]");
    const decBtn = event.target.closest("[data-byob-dec]");
    if (!incBtn && !decBtn) return;

    // Quick view opens from anywhere else on the card; the add controls must not.
    event.preventDefault();
    event.stopPropagation();

    if (incBtn) this.addPick(card.dataset.variantId);
    else this.removeLastPick(card.dataset.variantId);
  }

  navigationClickHandler(event) {
    if (event.target.closest("[data-add-bundle-to-cart]")) {
      this.addToCart();
      return;
    }

    const slot = event.target.closest(".bundler-builder--nav-item");
    if (slot && slot.dataset.navItemHasContent === "true") {
      this.removePickAt(Number(slot.dataset.navItemIndex) - 1);
    }
  }

  barClickHandler(event) {
    if (event.target.closest("[data-byob-bar-cta]")) {
      this.addToCart();
      return;
    }

    if (event.target.closest("[data-byob-bar-toggle]")) {
      this.setBarExpanded(this.mobileBar.dataset.expanded !== "true");
      return;
    }

    const slot = event.target.closest("[data-byob-bar-slot-index]");
    if (!slot || slot.dataset.navItemHasContent !== "true") return;

    // Collapsed tiles are only 48px, so a stray tap must not drop a pick —
    // open the sheet instead and let the shopper use the explicit remove.
    if (this.mobileBar.dataset.expanded !== "true") {
      this.setBarExpanded(true);
      return;
    }

    if (event.target.closest("[data-byob-bar-remove]")) {
      this.removePickAt(Number(slot.dataset.byobBarSlotIndex) - 1);
    }
  }

  setBarExpanded(expanded) {
    if (!this.mobileBar) return;
    this.mobileBar.dataset.expanded = expanded ? "true" : "false";
    if (this.barToggle) {
      this.barToggle.setAttribute("aria-expanded", expanded ? "true" : "false");
    }
  }

  /* ──────────────────────────────
     Selection state
     ────────────────────────────── */
  getCard(variantId) {
    return this.productsGrid.querySelector(
      `.bundler-builder--grid-item[data-variant-id="${variantId}"]`
    );
  }

  addPick(variantId) {
    if (!variantId) return false;
    if (this.picks.length >= this.maxSlots) return false;

    const card = this.getCard(variantId);
    if (!card) return false;

    this.picks.push({
      variantId: String(variantId),
      priceCents: parseInt(card.dataset.priceCents, 10) || 0,
      sellingPlanId: card.dataset.sellingPlanId || "",
      title: card.dataset.productTitle || "",
      imageSrc: card.dataset.productImage || "",
    });
    this.render();
    return true;
  }

  removePickAt(index) {
    if (index < 0 || index >= this.picks.length) return;
    this.picks.splice(index, 1);
    this.render();
  }

  removeLastPick(variantId) {
    const index = this.picks
      .map((pick) => pick.variantId)
      .lastIndexOf(String(variantId));
    if (index === -1) return;
    this.removePickAt(index);
  }

  /* The cart's bundle card links back here with ?edit_set=<_bundle_id>, so the
     shopper keeps editing the set they already built instead of starting over. */
  async hydrateFromCart(bundleId) {
    let cart;

    try {
      const response = await fetch(window.Shopify.routes.root + "cart.js");
      if (!response.ok) throw new Error("Could not read the cart");
      cart = await response.json();
    } catch (error) {
      console.error(`Bundler Build | hydrateFromCart | ${error}`);
      return;
    }

    const lines = (cart.items || []).filter(
      (line) => (line.properties || {})._bundle_id === bundleId
    );
    // The set may have been removed in another tab; fall back to a fresh build.
    if (!lines.length) return;

    this.picks = [];
    lines.forEach((line) => {
      // addPick ignores variants that are no longer in the grid, so a product
      // pulled from the collection just drops out of the restored set.
      for (let unit = 0; unit < line.quantity; unit += 1) {
        this.addPick(String(line.variant_id));
      }
    });

    // Only the id is kept: this shop re-keys cart lines on every mutation, so a
    // key read now would point at a different line by the time we remove it.
    this.editing = { bundleId };
    this.dataset.editing = "true";
    this.render();
  }

  /* Public API — used by the quick view modal */
  addProduct(variantId) {
    return this.addPick(variantId);
  }

  getBundleCount() {
    return this.picks.length;
  }

  isFull() {
    return this.picks.length >= this.maxSlots;
  }

  /* ──────────────────────────────
     Pricing
     ────────────────────────────── */
  getActiveDiscount() {
    const count = this.picks.length;
    return this.tiers.reduce(
      (discount, tier) => (count >= tier.items ? tier.discount : discount),
      0
    );
  }

  getNextTier() {
    const count = this.picks.length;
    return this.tiers.find((tier) => count < tier.items) || null;
  }

  getTotals() {
    const subtotalCents = this.picks.reduce(
      (sum, pick) => sum + pick.priceCents,
      0
    );
    const discount = this.getActiveDiscount();
    const savingsCents = Math.round(subtotalCents * (discount / 100));

    return {
      count: this.picks.length,
      discount,
      subtotalCents,
      savingsCents,
      totalCents: subtotalCents - savingsCents,
    };
  }

  formatMoney(cents) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format((cents || 0) / 100);
  }

  /* ──────────────────────────────
     Rendering
     ────────────────────────────── */
  render() {
    this.renderCards();
    this.renderSlots();
    this.renderSummary();
  }

  renderCards() {
    const counts = this.picks.reduce((acc, pick) => {
      acc[pick.variantId] = (acc[pick.variantId] || 0) + 1;
      return acc;
    }, {});
    const full = this.isFull();

    this.productsGrid
      .querySelectorAll(".bundler-builder--grid-item[data-variant-id]")
      .forEach((card) => {
        const stepper = card.querySelector("[data-byob-qty]");
        if (!stepper) return;

        const qty = counts[card.dataset.variantId] || 0;
        stepper.dataset.qty = qty;
        card.dataset.selected = qty > 0 ? "true" : "false";

        const value = stepper.querySelector("[data-byob-qty-value]");
        if (value) value.textContent = qty;

        const incBtn = stepper.querySelector("[data-byob-inc]");
        if (incBtn) incBtn.disabled = full;

        const addBtn = card.querySelector("[data-byob-add]");
        if (addBtn) {
          addBtn.disabled = full;
          addBtn.textContent = full ? "Bundle full" : "Add to bundle";
        }
      });
  }

  renderSlots() {
    this.slots.forEach((slot, index) => {
      this.paintSlot(slot, this.picks[index]);

      const barSlot = this.barSlots
        ? this.barSlots.querySelector(
            `[data-byob-bar-slot-index="${index + 1}"]`
          )
        : null;
      if (barSlot) this.paintBarSlot(barSlot, this.picks[index]);
    });
  }

  paintSlot(slot, pick) {
    const label = slot.querySelector(".byob-slot__label");
    const existingThumb = slot.querySelector(".byob-slot__thumb");
    const existingRemove = slot.querySelector(".byob-slot__remove");

    if (existingThumb) existingThumb.remove();
    if (existingRemove) existingRemove.remove();

    if (!pick) {
      slot.dataset.navItemHasContent = "false";
      slot.dataset.navItemProdId = "";
      if (label) label.textContent = "Select an item";
      return;
    }

    slot.dataset.navItemHasContent = "true";
    slot.dataset.navItemProdId = pick.variantId;

    const thumb = document.createElement("img");
    thumb.className = "byob-slot__thumb";
    thumb.src = pick.imageSrc;
    thumb.alt = pick.title;
    thumb.loading = "lazy";
    slot.prepend(thumb);

    if (label) label.textContent = pick.title;

    const remove = document.createElement("span");
    remove.className = "byob-slot__remove";
    remove.setAttribute("aria-hidden", "true");
    remove.textContent = "×";
    slot.appendChild(remove);
  }

  paintBarSlot(barSlot, pick) {
    const media = barSlot.querySelector("[data-byob-bar-slot-media]");
    const label = barSlot.querySelector(".byob-bar-slot__label");
    const existingRemove = barSlot.querySelector("[data-byob-bar-remove]");

    if (media) media.innerHTML = "";
    if (existingRemove) existingRemove.remove();

    if (!pick) {
      barSlot.dataset.navItemHasContent = "false";
      if (label) label.textContent = "Select an item";
      return;
    }

    barSlot.dataset.navItemHasContent = "true";

    if (media) {
      const thumb = document.createElement("img");
      thumb.src = pick.imageSrc;
      thumb.alt = pick.title;
      thumb.loading = "lazy";
      media.appendChild(thumb);
    }

    if (label) label.textContent = pick.title;

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "byob-bar-slot__remove";
    remove.setAttribute("data-byob-bar-remove", "");
    remove.setAttribute("aria-label", `Remove ${pick.title} from bundle`);
    remove.textContent = "×";
    barSlot.appendChild(remove);
  }

  getProgressText() {
    const { count, discount } = this.getTotals();
    const nextTier = this.getNextTier();

    if (!nextTier) {
      return discount > 0 ? `You unlocked ${discount}% off!` : "";
    }

    const remaining = nextTier.items - count;
    const noun = remaining === 1 ? "item" : "items";
    const prefix = discount > 0 ? `You unlocked ${discount}% off. ` : "";

    return `${prefix}Add ${remaining} more ${noun} to save ${nextTier.discount}%`;
  }

  /* The bar spans an empty bundle to the highest tier, so it reads full once
     the best discount is unlocked even if slots remain. */
  renderTierProgress(count) {
    if (!this.tiers.length) return;

    const maxTierItems = this.tiers[this.tiers.length - 1].items;
    const ratio = Math.min(count / maxTierItems, 1);

    this.progressFills.forEach((fill) => {
      fill.style.width = `${ratio * 100}%`;
    });

    this.tierMarkers.forEach((marker) => {
      const threshold = Number(marker.dataset.byobTierMarker);
      marker.dataset.reached = count >= threshold ? "true" : "false";
    });
  }

  renderSummary() {
    const { count, discount, subtotalCents, savingsCents, totalCents } =
      this.getTotals();
    const isValid = count >= this.minItems;
    const compareText = count > 0 && discount > 0 ? this.formatMoney(subtotalCents) : "";
    const totalText = this.formatMoney(totalCents);
    const progressText = this.getProgressText();
    const ctaText = isValid
      ? `${this.editing ? "UPDATE MY SET" : "ADD TO CART"} - ${totalText}`
      : "ADD MORE";

    if (this.progressEl) this.progressEl.textContent = progressText;
    if (this.barProgress) this.barProgress.textContent = progressText;

    this.renderTierProgress(count);

    if (this.summary) {
      this.summary.hidden = !(count > 0 && discount > 0);
      if (this.discountLabelEl) {
        this.discountLabelEl.textContent = `Discount (${discount}%)`;
      }
      if (this.savingsEl) {
        this.savingsEl.textContent = `-${this.formatMoney(savingsCents)}`;
      }
    }

    if (this.addToCartBtn) this.addToCartBtn.disabled = !isValid;
    if (this.ctaLabel) this.ctaLabel.textContent = ctaText;
    if (this.compareEl) {
      this.compareEl.textContent = isValid ? compareText : "";
    }

    if (this.barCount) {
      this.barCount.textContent = `${count} product${
        count === 1 ? "" : "s"
      } chosen`;
    }
    if (this.barCompare) this.barCompare.textContent = compareText;
    if (this.barCta) this.barCta.disabled = !isValid;
    if (this.barCtaLabel) this.barCtaLabel.textContent = ctaText;
  }

  /* ──────────────────────────────
     Cart
     ────────────────────────────── */
  getBundleProperties() {
    if (!this.bundlePropertyKey) return null;

    return {
      [this.bundlePropertyKey]: this.bundlePropertyValue || "",
      // Stamps this specific set so the cart can group it separately from
      // any other bundle the shopper builds in the same session.
      _bundle_id: `byob-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`,
    };
  }

  getCartLineItems() {
    const properties = this.getBundleProperties();
    const byVariant = new Map();

    this.picks.forEach((pick) => {
      const existing = byVariant.get(pick.variantId);
      if (existing) {
        existing.quantity += 1;
        return;
      }
      byVariant.set(pick.variantId, {
        id: pick.variantId,
        quantity: 1,
        ...(properties ? { properties } : {}),
      });
    });

    return Array.from(byVariant.values());
  }

  async addToCart() {
    if (this.picks.length < this.minItems) return;

    this.setLoadingStateAddToCartBtn(true);

    try {
      const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ items: this.getCartLineItems() }),
      });

      if (!response.ok) throw new Error("Error adding products to cart");

      await this.dropEditedSet();
    } catch (error) {
      console.error(`Bundler Build | addToCart | ${error}`);
    } finally {
      this.setLoadingStateAddToCartBtn(false);
      this.picks = [];
      this.render();
      openCartDrawer(true);
    }
  }

  /* Runs only after the replacement set is safely in the cart. The new lines
     carry a fresh _bundle_id, so Shopify can never merge them into the ones
     being dropped here. Keys are re-read from the cart first — they are not
     stable across mutations, and a stale one can point at the new set. */
  async dropEditedSet() {
    if (!this.editing) return;

    const { bundleId } = this.editing;

    try {
      const response = await fetch(window.Shopify.routes.root + "cart.js");
      if (!response.ok) throw new Error("Could not read the cart");
      const cart = await response.json();

      const updates = {};
      (cart.items || []).forEach((line) => {
        if ((line.properties || {})._bundle_id === bundleId) {
          updates[line.key] = 0;
        }
      });

      if (Object.keys(updates).length) {
        await fetch(window.Shopify.routes.root + "cart/update.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ updates }),
        });
      }
    } catch (error) {
      console.error(`Bundler Build | dropEditedSet | ${error}`);
    }

    this.editing = null;
    delete this.dataset.editing;

    const url = new URL(window.location.href);
    url.searchParams.delete("edit_set");
    window.history.replaceState({}, "", url);
  }

  setLoadingStateAddToCartBtn(flag) {
    if (this.addToCartBtn) this.addToCartBtn.disabled = flag;
    if (this.barCta) this.barCta.disabled = flag;
  }
}
customElements.define("bundler-builder", BundlerBuilder);
